// Floating widget rendered while a Twilio PSTN call is calling / in-call.
// Hidden when idle. Fixed position top-right, mirrors the WhatsApp
// CallControl widget so agents see a consistent UI across call types.
//
// Controls:
//   - Mute / Unmute
//   - Show DTMF keypad → send digits during the call
//   - Hang up

import React, { useState } from "react";
import {
  Avatar,
  Box,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import DialpadIcon from "@mui/icons-material/Dialpad";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { useTwilioVoice } from "src/context/twilioVoice/TwilioVoiceContext";

const DTMF_KEYS: string[][] = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

const ActiveCallWidget: React.FC = () => {
  const {
    callState,
    remoteParty,
    remotePartyName,
    formattedDuration,
    isMuted,
    hangup,
    toggleMute,
    sendDigit,
  } = useTwilioVoice();
  const [dtmfOpen, setDtmfOpen] = useState(false);

  const isVisible = callState === "calling" || callState === "in-call";
  if (!isVisible) return null;

  const statusLabel =
    callState === "calling" ? "Calling…" : formattedDuration || "Connected";

  const hasName = Boolean(remotePartyName);
  const primaryLine = hasName ? remotePartyName : remoteParty || "Outbound";
  const secondaryLine = hasName ? remoteParty : null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        top: 80,
        right: 40,
        zIndex: 9999,
        p: 2,
        width: 280,
        borderRadius: 4,
        background: "rgba(255, 255, 255, 0.97)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Avatar
        sx={{
          width: 64,
          height: 64,
          mb: 1,
          border: "2px solid #F22F46",
          bgcolor: "rgba(242, 47, 70, 0.08)",
        }}
      >
        <LocalPhoneIcon sx={{ color: "#F22F46", fontSize: 28 }} />
      </Avatar>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {primaryLine}
      </Typography>
      {secondaryLine && (
        <Typography
          variant="caption"
          sx={{ color: "rgba(0,0,0,0.55)", fontFamily: "monospace" }}
        >
          {secondaryLine}
        </Typography>
      )}
      <Typography variant="caption" color="textSecondary" sx={{ mb: 1.5 }}>
        {statusLabel}
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
        <Tooltip title={isMuted ? "Unmute" : "Mute"}>
          <IconButton
            onClick={toggleMute}
            sx={{
              bgcolor: isMuted ? "#F22F46" : "rgba(0,0,0,0.06)",
              color: isMuted ? "white" : "rgba(0,0,0,0.7)",
              "&:hover": {
                bgcolor: isMuted ? "#c91f33" : "rgba(0,0,0,0.12)",
              },
              width: 44,
              height: 44,
            }}
          >
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title={dtmfOpen ? "Hide keypad" : "Show keypad"}>
          <IconButton
            onClick={() => setDtmfOpen((p) => !p)}
            sx={{
              bgcolor: dtmfOpen ? "#F22F46" : "rgba(0,0,0,0.06)",
              color: dtmfOpen ? "white" : "rgba(0,0,0,0.7)",
              "&:hover": {
                bgcolor: dtmfOpen ? "#c91f33" : "rgba(0,0,0,0.12)",
              },
              width: 44,
              height: 44,
            }}
          >
            <DialpadIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Hang up">
          <IconButton
            onClick={hangup}
            sx={{
              bgcolor: "#FF3B30",
              color: "white",
              "&:hover": { bgcolor: "#D70015" },
              width: 44,
              height: 44,
            }}
          >
            <CallEndIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={dtmfOpen} sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 0.75,
            mt: 1,
          }}
        >
          {DTMF_KEYS.flat().map((digit) => (
            <IconButton
              key={digit}
              onClick={() => sendDigit(digit)}
              sx={{
                bgcolor: "rgba(0,0,0,0.04)",
                color: "#1a1a1a",
                borderRadius: 2,
                fontWeight: 700,
                fontSize: "1.1em",
                py: 0.75,
                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
              }}
              disableRipple={false}
            >
              {digit}
            </IconButton>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ActiveCallWidget;
